import { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import * as yup from 'yup';
import Autocomplete from '@mui/lab/Autocomplete';
import { createFilterOptions } from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import { tableCellClasses } from '@mui/material/TableCell';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { styled, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import {
  useAddOrEditPatientDiagnosisMutation,
  useDeletePatientDiagnosisMutation,
  useGetDiagnosesQuery,
  useGetPatientDiagnosesQuery,
  useGetPatientDiagnosisInfoQuery,
} from '../../app/services/patient';
import { useToast } from '../../app/toast';
import DataList from '../../common/components/DataList';
import DataTable from '../../common/components/DataTable';
import Loading from '../../common/components/Loading';
import PageHeader from '../../common/components/PageHeader';
import PopupModal from '../../common/components/PopupModal';
import RoundedContainer from '../../common/components/RoundedContainer';
import SubmitButton from '../../common/components/SubmitButton';
import DeleteReportIcon from '../../common/components/icons/DeleteReportIcon';
import EditUserIcon from '../../common/components/icons/EditInterviewIcon';
import DashboardLayout from '../../common/layouts/Dashboard';
import {
  selectContentPopup,
  selectIsOpenPopup,
  setContentPopup,
  setIsOpenPopup,
} from '../../common/slices/globalSlice';
import { formatServerDateAndTime } from '../../common/utils/dateTimeUtils';
import { addBreadcrumb, editBreadcrumbs, ReplaceType } from '../../common/utils/editBreadcrums';
import { getPageTitle } from '../../common/utils/pageUtils';
import { StyledClinicianNote } from '../../features/interview/interviewUtils';
import { PageProps } from '../../types/PageProps';
import {
  CommonItem,
  ICDOptionItem,
  PatientDiagnosisInfo,
  PrimaryOptionItem,
} from '../../types/PatientRequest';
import { TimeframeProperties } from '../../types/PatientType';
import { PopupContent } from '../../types/PopupType';
import { ResponseError } from '../../types/ResponseError';
import ContextMenu from './ContextMenu';

const StyleTextField = styled(TextField)(({ theme }) => ({
  [`&.MuiFormControl-root`]: {
    width: '100%',

    [theme.breakpoints.up('md')]: {
      width: '49%',
    },
  },
}));

const StyleAutocomplete = styled(Autocomplete)(({ theme }) => ({
  width: '49%',
  '& .MuiOutlinedInput-root': {
    padding: '10px',
  },
  [theme.breakpoints.down('md')]: {
    width: '100%',
  },
}));

interface FormType {
  primaryDiagnosis: string;
  diagnosis: string;
  timeFrame: string;
  otherDiagnosis: string;
  suggestedIcdCode: string;
  otherIcdCode: string;
}

const defaultFormValues: FormType = {
  primaryDiagnosis: '',
  diagnosis: '',
  timeFrame: '',
  otherDiagnosis: '',
  suggestedIcdCode: '',
  otherIcdCode: '',
};

const primaryDiagnosisOptions = [
  { value: 'primary', label: 'Primary' },
  { value: 'secondary', label: 'Secondary' },
  { value: 'tertiary', label: 'Tertiary' },
  { value: 'quaternary', label: 'Quaternary' },
  { value: 'other', label: 'Other' },
];

const getOrder = (primaryDiagnosis: string) => {
  const order = primaryDiagnosisOptions.find((x) => x.value === primaryDiagnosis);
  return order ? order.label : '';
};

const getIcdCode = (patientDiagnoses: PatientDiagnosisInfo, t: any) => {
  return patientDiagnoses.suggestedIcdCode === t('patient.providerFeedback.form.otherIcdCodeValue')
    ? patientDiagnoses.otherIcdCode
    : patientDiagnoses.suggestedIcdCode;
};

const sortedPatientDiagnoses = (patientDiagnoses: PatientDiagnosisInfo[]) => {
  const orderMap = new Map(primaryDiagnosisOptions.map((o, i) => [o.value, i]));
  const sortedPatientDiagnoses = [...patientDiagnoses].sort((a, b) => {
    const primaryDiagnosisA = orderMap.get(a.primaryDiagnosis) ?? Infinity;
    const primaryDiagnosisB = orderMap.get(b.primaryDiagnosis) ?? Infinity;

    if (primaryDiagnosisA !== primaryDiagnosisB) {
      return primaryDiagnosisA - primaryDiagnosisB; // sort by primaryDiagnosis order first
    }

    // If same primaryDiagnosis, sort by createdAt (desc)
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });

  return sortedPatientDiagnoses;
};

const renderMobileItem = (item: PatientDiagnosisInfo, t: any) => {
  return [
    [
      t('patient.providerFeedback.headers.diagnosis'),
      item.diagnosisName ? item.diagnosisName : item.otherDiagnosis,
    ],
    [t('patient.providerFeedback.headers.timeframe'), item.timeFrame],
    [t('patient.providerFeedback.headers.icd10'), getIcdCode(item, t)],
    [t('patient.providerFeedback.headers.date'), formatServerDateAndTime(item.createdAt)],
  ];
};

export default function ProviderFeedbackPage({ breadcrumbs }: PageProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const { patientId } = useParams();
  const { state } = useLocation();
  const { fromPage } = !!state && state;
  const dispatch = useAppDispatch();
  const isOpenPopup = useAppSelector(selectIsOpenPopup);
  const contentPopup = useAppSelector(selectContentPopup);

  const [pageErrors, setPageErrors] = useState<string[]>([]);
  const [disabledOtherForm, setDisabledOtherForm] = useState<boolean>(true);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [patientIdAction, setPatientIdAction] = useState<string>('');
  const [diagnosisIdAction, setDiagnosisIdAction] = useState<number>(0);
  const [diagnoses, setDiagnosesData] = useState<CommonItem[]>([]);
  const [timeFramesData, setTimeFramesData] = useState<Record<string, string>[]>([]);
  const [suggestedIcdCodes, setSuggestedIcdCodes] = useState<ICDOptionItem[]>([]);
  const [disabledOtherIcdCodes, setDisabledOtherIcdCodes] = useState<boolean>(true);

  const replacePath: ReplaceType[] = [{ param: ':patientId', value: patientId! }];
  const diagnosisId = searchParams.get('diagnosisId');
  const formRef = useRef<HTMLDivElement>(null);

  const [addOrEditPatientDiagnosis] = useAddOrEditPatientDiagnosisMutation();
  const [deletePatientDiagnosis] = useDeletePatientDiagnosisMutation();

  const deletePatientDiagnosisContentPopup = {
    title: t('patient.providerFeedback.deletePatientDiagnosisContent.title'),
    description: t('patient.providerFeedback.deletePatientDiagnosisContent.description'),
    btnOk: t('patient.providerFeedback.deletePatientDiagnosisContent.btnOk'),
    btnClose: t('patient.providerFeedback.deletePatientDiagnosisContent.btnClose'),
    toastMessage: t('patient.providerFeedback.deletePatientDiagnosisContent.toastMessage'),
  } as PopupContent;

  const { data: diagnosesData, isLoading: isLoadingDiagnoses } = useGetDiagnosesQuery(
    !!patientId ? { patientId, includeIcdCodes: true } : skipToken
  );

  const {
    data: patientData,
    isLoading: isLoadingPatientDiagnoses,
    refetch: refetchPatientDiagnosis,
  } = useGetPatientDiagnosesQuery(!!patientId ? { patientId } : skipToken);

  const { data: diagnosesInfoData, isLoading: isLoadingDiagnosesInfoData } =
    useGetPatientDiagnosisInfoQuery(
      !!patientId && !!diagnosisId
        ? { patientId, patientDiagnosisId: parseInt(diagnosisId) }
        : skipToken
    );

  const validationSchema = yup
    .object({
      primaryDiagnosis: yup.string().trim().required(t('patient.error.blankPrimaryDiagnosis')),
      diagnosis: yup.string().required(t('patient.error.blankDiagnosis')),
      timeFrame: yup.string().when('diagnosis', {
        is: (diagnosis: string) => diagnosis === '0' || timeFramesData.length === 0,
        then: yup.string().trim(),
        otherwise: yup.string().trim().required(t('patient.error.blankTimeFrame')),
      }),
      otherDiagnosis: yup.string().when('diagnosis', {
        is: '0',
        then: yup.string().trim().required(t('patient.error.blankOtherDiagnosis')),
        otherwise: yup.string().trim(),
      }),
      suggestedIcdCode: yup.string().trim(),
      otherIcdCode: yup
        .string()
        .trim()
        .when('suggestedIcdCode', {
          is: (suggestedIcdCode: string) =>
            suggestedIcdCode === t('patient.providerFeedback.form.otherIcdCodeValue'),
          then: yup.string().trim().required(t('patient.error.blankOtherIcdCode')),
          otherwise: yup.string().trim(),
        }),
    })
    .required();

  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
    reset,
    resetField,
    clearErrors,
  } = useForm<FormType>({
    resolver: yupResolver(validationSchema),
    defaultValues: defaultFormValues,
  });

  const showDeletePatientDiagnosisPopup = async (patientId: string, diagnosisId: number) => {
    dispatch(setContentPopup({ content: deletePatientDiagnosisContentPopup }));
    dispatch(setIsOpenPopup({ value: true }));
    setPatientIdAction(patientId);
    setDiagnosisIdAction(diagnosisId);
    handleClose();
  };

  useEffect(() => {
    if (diagnosesData) {
      const newDiagnosesData = [...diagnosesData.diagnoses];
      newDiagnosesData.push({
        id: 0,
        name: t('patient.providerFeedback.form.otherDiagnosis'),
        availableTimeframesFull: diagnosesData.otherDiagnosesTimeFramesFull,
      });
      setDiagnosesData(newDiagnosesData);
    }
  }, [clearErrors, diagnosesData, t]);

  useEffect(() => {
    if (diagnosesInfoData) {
      const bindEditControls = () => {
        if (!!diagnosesInfoData.primaryDiagnosis) {
          setValue('primaryDiagnosis', diagnosesInfoData.primaryDiagnosis);
        }

        if (diagnosesInfoData.diagnosisId) {
          setValue('diagnosis', diagnosesInfoData.diagnosisId.toString());
        } else {
          setValue('diagnosis', '0');
          setDisabledOtherForm(false);
        }

        const icdCodes = [];
        if (diagnosesInfoData.timeFrame) {
          setValue('timeFrame', diagnosesInfoData.timeFrame);
          setTimeFramesData(
            diagnosesInfoData.availableTimeframes.filter(
              (item) => item[TimeframeProperties.Timeframe]
            )
          );
          const timeframe = diagnosesInfoData.availableTimeframes.find(
            (item) =>
              item[TimeframeProperties.Timeframe] === diagnosesInfoData.timeFrame ||
              !item[TimeframeProperties.Timeframe]
          );
          if (timeframe && timeframe[TimeframeProperties.ICDCode]) {
            icdCodes.push({
              label: timeframe[TimeframeProperties.ICDCode],
              value: timeframe[TimeframeProperties.ICDCode],
            });
          }
        }
        icdCodes.push({
          label: t('patient.providerFeedback.form.otherIcdCodeLabel'),
          value: t('patient.providerFeedback.form.otherIcdCodeValue'),
        });
        setSuggestedIcdCodes(icdCodes);

        if (diagnosesInfoData.suggestedIcdCode) {
          setValue('suggestedIcdCode', diagnosesInfoData.suggestedIcdCode);
          if (
            diagnosesInfoData.suggestedIcdCode ===
            t('patient.providerFeedback.form.otherIcdCodeValue')
          ) {
            setDisabledOtherIcdCodes(false);
            setValue('otherIcdCode', diagnosesInfoData.otherIcdCode);
          }
        }

        setValue('otherDiagnosis', diagnosesInfoData.otherDiagnosis);
      };

      bindEditControls();
    }

    (formRef.current as HTMLDivElement).scrollIntoView({ behavior: 'smooth' });
  }, [diagnosesInfoData, setValue, t]);

  const filterDiagnosisOptions = createFilterOptions({
    matchFrom: 'any',
    stringify: (option: any) => getDiagnosisOptionLabel(option),
  });

  const filterPrimaryOptions = createFilterOptions({
    matchFrom: 'any',
    stringify: (option: any) => getPrimaryOptionLabel(option),
  });

  const filterTimeFrameOptions = createFilterOptions({
    matchFrom: 'any',
    stringify: (option: any) => getTimeFrameOptionLabel(option),
  });

  const getDiagnosisOptionLabel = (option: any) => option.name;
  const getPrimaryOptionLabel = (option: any) => option.label;
  const getTimeFrameOptionLabel = (option: any) => option[TimeframeProperties.Timeframe];

  const handlePrimaryDiagnosisChange = (
    _event: React.SyntheticEvent<Element, Event>,
    value: PrimaryOptionItem | null
  ) => {
    if (!value) {
      resetField('primaryDiagnosis');
    } else {
      const selectedValue = value.value;
      setValue('primaryDiagnosis', selectedValue);
    }
  };

  const handleDiagnosisChange = (
    _event: React.SyntheticEvent<Element, Event>,
    value: CommonItem | null
  ) => {
    if (!value) {
      setTimeFramesData([]);
    } else {
      const selectedValue = value.id;
      const availableTimeFrames = (value.availableTimeframesFull || []).filter(
        (item) => !!item[TimeframeProperties.Timeframe]
      );
      const icdCodes: ICDOptionItem[] = [];
      if (value.availableTimeframesFull) {
        const haveUndefinedTimeframe = value.availableTimeframesFull.some(
          (item) => !item[TimeframeProperties.Timeframe]
        );
        if (haveUndefinedTimeframe) {
          value.availableTimeframesFull.forEach((item) => {
            if (
              item[TimeframeProperties.ICDCode] &&
              !icdCodes.some((icd) => icd.value === item[TimeframeProperties.ICDCode])
            ) {
              icdCodes.push({
                label: item[TimeframeProperties.ICDCode],
                value: item[TimeframeProperties.ICDCode],
              });
            }
          });
          icdCodes.push({
            label: t('patient.providerFeedback.form.otherIcdCodeLabel'),
            value: t('patient.providerFeedback.form.otherIcdCodeValue'),
          });
        }
      }

      setDisabledOtherForm(true);
      if (selectedValue === 0) {
        setDisabledOtherForm(false);
      }
      setTimeFramesData(availableTimeFrames || []);
      setSuggestedIcdCodes(icdCodes);

      setValue('diagnosis', selectedValue.toString());
    }
    resetField('suggestedIcdCode');
    resetField('otherIcdCode');
    resetField('otherDiagnosis');
    resetField('timeFrame');
  };

  const handleTimeframeChange = (
    _event: React.SyntheticEvent<Element, Event>,
    value: Record<string, string> | null
  ) => {
    if (!value) {
      resetField('suggestedIcdCode');
      resetField('otherIcdCode');
      resetField('timeFrame');
    } else {
      const selectedValue = value[TimeframeProperties.Timeframe];
      const icdCodes: ICDOptionItem[] = [];
      if (value[TimeframeProperties.ICDCode]) {
        icdCodes.push({
          label: value[TimeframeProperties.ICDCode],
          value: value[TimeframeProperties.ICDCode],
        });
      }
      icdCodes.push({
        label: t('patient.providerFeedback.form.otherIcdCodeLabel'),
        value: t('patient.providerFeedback.form.otherIcdCodeValue'),
      });
      setSuggestedIcdCodes(icdCodes);
      setValue('timeFrame', selectedValue);
    }
  };

  const handleSuggestedIcdCodeChange = (
    _event: React.SyntheticEvent<Element, Event>,
    value: any
  ) => {
    const icdValue = value as ICDOptionItem | null;
    const selectedValue = icdValue?.value || '';
    setValue('suggestedIcdCode', selectedValue);

    const isOtherIcd = selectedValue === t('patient.providerFeedback.form.otherIcdCodeValue');

    setDisabledOtherIcdCodes(!isOtherIcd);

    if (!selectedValue) {
      resetField('suggestedIcdCode');
      resetField('otherIcdCode');
    }
  };

  const handleEdit = (data: PatientDiagnosisInfo) => {
    navigate(`/dashboard/patient/${patientId}/diagnosis?diagnosisId=${data.patientDiagnosisId}`);
    setAnchorEl(null);
    clearErrors(['diagnosis', 'timeFrame', 'otherDiagnosis']);
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const renderContextMenu = (data: PatientDiagnosisInfo) => {
    const menuItems = [
      {
        isDisplayed: true,
        label: t('patient.providerFeedback.form.edit'),
        icon: <EditUserIcon />,
        onClick: () => handleEdit(data),
      },
      {
        isDisplayed: true,
        label: t('patient.providerFeedback.form.delete'),
        icon: <DeleteReportIcon />,
        onClick: () => showDeletePatientDiagnosisPopup(data.patientId, data.patientDiagnosisId),
      },
    ];

    return (
      <ContextMenu
        id={`${data.patientDiagnosisId}`}
        anchorEl={anchorEl}
        handleClick={handleClick}
        handleClose={handleClose}
        menuItems={menuItems}
      />
    );
  };

  const renderDesktopData = (data: PatientDiagnosisInfo[]) => {
    return (
      <DataTable
        commonCellStyles={{
          [`&.${tableCellClasses.head}`]: {
            padding: '16px 8px',
            '&:first-of-type': {
              paddingLeft: '24px',
            },
          },
          [`&.${tableCellClasses.body}`]: {
            padding: '16px 8px',
            '&:first-of-type': {
              paddingLeft: '24px',
            },
          },
        }}
        headers={[
          { text: t('patient.providerFeedback.headers.order'), sx: { width: '15%' } },
          { text: t('patient.providerFeedback.headers.diagnosis'), sx: { width: '30%' } },
          { text: t('patient.providerFeedback.headers.timeframe'), sx: { width: '15%' } },
          { text: t('patient.providerFeedback.headers.icd10'), sx: { width: '15%' } },
          { text: t('patient.providerFeedback.headers.date'), sx: { width: '20%' } },
          {
            text: t('patient.providerFeedback.headers.action'),
            sx: { textAlign: 'center', width: '5%' },
          },
        ]}
        items={data.map((item) => [
          { component: <Typography>{getOrder(item.primaryDiagnosis)}</Typography> },
          {
            component: (
              <Typography>
                {item.diagnosisName ? item.diagnosisName : item.otherDiagnosis}
              </Typography>
            ),
          },
          { component: <Typography>{item.timeFrame}</Typography> },
          { component: <Typography>{getIcdCode(item, t)}</Typography> },
          { component: <Typography>{formatServerDateAndTime(item.createdAt)}</Typography> },
          { component: renderContextMenu(item), sx: { textAlign: 'center' } },
        ])}
      />
    );
  };

  const renderMobileData = (data: PatientDiagnosisInfo[]) => {
    return (
      <DataList
        lineHeight={'auto'}
        data={data.map((item) => ({
          leftHeader: (
            <Box sx={{ display: 'flex', alignItems: 'center', fontWeight: 600 }}>
              {getOrder(item.primaryDiagnosis)}
            </Box>
          ),
          rightHeader: renderContextMenu(item),
          items: renderMobileItem(item, t),
        }))}
      />
    );
  };

  const handleDeletePatientDiagnosis = async (successCallback?: () => void) => {
    try {
      if (patientIdAction && diagnosisIdAction) {
        await deletePatientDiagnosis({
          patientId: patientIdAction,
          patientDiagnosisId: diagnosisIdAction,
        }).unwrap();
      }
      if (successCallback) {
        successCallback();
      }
      toast.publish(
        t('patient.providerFeedback.deletePatientDiagnosisContent.toastMessage'),
        'success'
      );
      navigate(`/dashboard/patient/${patientId}/diagnosis`);
      reset();
      setPageErrors([]);
      setTimeFramesData([]);
      setDisabledOtherForm(true);
      refetchPatientDiagnosis();
    } catch (e) {
      toast.publish(t('patient.error.cannotDeletePatientDiagnosis'), 'error');
    }
  };

  const onSubmit = async (data: FormType) => {
    const { diagnosis, timeFrame, otherDiagnosis } = data;
    const reqData = {
      patientId: patientId!,
      patientDiagnosisId: diagnosisId ? parseInt(diagnosisId) : null,
      primaryDiagnosis: data.primaryDiagnosis || null,
      diagnosis: parseInt(diagnosis) !== 0 ? parseInt(diagnosis) : null,
      timeFrame: timeFrame || null,
      suggestedIcdCode: data.suggestedIcdCode || null,
      otherIcdCode: data.otherIcdCode || null,
      otherDiagnosis,
    };
    try {
      const res = await addOrEditPatientDiagnosis(reqData).unwrap();
      if (res) {
        toast.publish(
          diagnosisId
            ? t('patient.providerFeedback.updateSuccess')
            : t('patient.providerFeedback.addSuccess'),
          'success',
          3000
        );
        if (diagnosisId) {
          navigate(`/dashboard/patient/${patientId}/diagnosis`);
        }

        reset();
        setPageErrors([]);
        setTimeFramesData([]);
        setSuggestedIcdCodes([]);
        setDisabledOtherForm(true);
        setDisabledOtherIcdCodes(true);
        refetchPatientDiagnosis();
      }
    } catch (e) {
      const {
        data: { error },
      } = e as ResponseError;
      setPageErrors(error);
    }
  };

  const isLoading = isLoadingDiagnoses || isLoadingPatientDiagnoses || isLoadingDiagnosesInfoData;

  return (
    <Box ref={formRef}>
      <DashboardLayout
        breadcrumbs={
          !fromPage
            ? editBreadcrumbs(breadcrumbs, ...replacePath)
            : addBreadcrumb(breadcrumbs!, fromPage)
        }
      >
        <Helmet>
          <title>{getPageTitle(t('patient.providerFeedback.title'))}</title>
        </Helmet>
        <Container maxWidth="xl">
          <PageHeader
            sx={{ mt: 1, mb: 2 }}
            headerText={t('patient.providerFeedback.title')}
            backUrl={`/dashboard/patient/${patientId}`}
          />
        </Container>
        <Container maxWidth="xl" disableGutters={true}>
          <RoundedContainer sx={{ pt: 1, mt: 2 }}>
            {isLoading ? (
              <Loading />
            ) : (
              <Box sx={{ mt: 2 }} component="form" noValidate onSubmit={handleSubmit(onSubmit)}>
                <Box
                  sx={{
                    py: 1,
                    display: 'flex',
                    justifyContent: 'space-between',
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                  }}
                >
                  <Controller
                    name="primaryDiagnosis"
                    control={control}
                    render={({ fieldState, formState, field: { value }, ...props }) => (
                      <Autocomplete
                        {...props}
                        options={primaryDiagnosisOptions}
                        getOptionLabel={getPrimaryOptionLabel}
                        fullWidth
                        value={
                          value
                            ? primaryDiagnosisOptions.find((item) => item.value === value) || null
                            : null
                        }
                        onChange={handlePrimaryDiagnosisChange}
                        filterOptions={filterPrimaryOptions}
                        renderOption={(
                          props: React.HTMLAttributes<HTMLLIElement>,
                          option: PrimaryOptionItem
                        ) => {
                          return (
                            <li {...props} key={option.value} value={option.value}>
                              {getPrimaryOptionLabel(option)}
                            </li>
                          );
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            error={!!errors.primaryDiagnosis}
                            helperText={errors.primaryDiagnosis?.message}
                            margin="normal"
                            fullWidth
                            label={t('patient.providerFeedback.form.primaryDiagnosis')}
                          />
                        )}
                      />
                    )}
                  />

                  <Controller
                    name="diagnosis"
                    control={control}
                    render={({ fieldState, formState, field: { value }, ...props }) => (
                      <Autocomplete
                        {...props}
                        fullWidth
                        options={diagnoses}
                        getOptionLabel={getDiagnosisOptionLabel}
                        onChange={handleDiagnosisChange}
                        value={
                          value
                            ? diagnoses?.find((item) => item.id === parseInt(value)) || null
                            : null
                        }
                        filterOptions={filterDiagnosisOptions}
                        renderOption={(
                          props: React.HTMLAttributes<HTMLLIElement>,
                          option: CommonItem
                        ) => {
                          return (
                            <li {...props} key={option.id} value={option.id}>
                              {getDiagnosisOptionLabel(option)}
                            </li>
                          );
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            error={!!errors.diagnosis}
                            helperText={errors.diagnosis?.message}
                            margin="normal"
                            fullWidth
                            label={t('patient.providerFeedback.form.diagnosis')}
                          />
                        )}
                      />
                    )}
                  />

                  <Controller
                    name="timeFrame"
                    control={control}
                    render={({ fieldState, formState, field: { value }, ...props }) => (
                      <Autocomplete
                        {...props}
                        fullWidth
                        options={timeFramesData}
                        getOptionLabel={getTimeFrameOptionLabel}
                        onChange={handleTimeframeChange}
                        disabled={timeFramesData.length === 0}
                        value={
                          value
                            ? timeFramesData?.find(
                                (item) => item[TimeframeProperties.Timeframe] === value
                              ) || null
                            : null
                        }
                        filterOptions={filterTimeFrameOptions}
                        renderOption={(
                          props: React.HTMLAttributes<HTMLLIElement>,
                          option: Record<string, string>
                        ) => {
                          return (
                            <li
                              {...props}
                              key={option[TimeframeProperties.Timeframe]}
                              value={option[TimeframeProperties.Timeframe]}
                            >
                              {getTimeFrameOptionLabel(option)}
                            </li>
                          );
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            error={!!errors.timeFrame}
                            helperText={errors.timeFrame?.message}
                            margin="normal"
                            fullWidth
                            label={t('patient.providerFeedback.form.timeFrames')}
                          />
                        )}
                      />
                    )}
                  />

                  <Controller
                    name="otherDiagnosis"
                    control={control}
                    render={({ field }) => (
                      <StyledClinicianNote
                        {...field}
                        sx={{
                          backgroundColor: theme.xPalette.white,
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: theme.xPalette.noteLightGrey,
                            borderRadius: '8px',
                            pb: 4,
                          },
                        }}
                        disabled={disabledOtherForm}
                        multiline
                        maxRows={4}
                        fullWidth
                        label={t('patient.providerFeedback.form.otherDiagnosis')}
                        error={!!errors.otherDiagnosis}
                        helperText={errors.otherDiagnosis?.message}
                        variant="outlined"
                      />
                    )}
                  />

                  <Controller
                    name="suggestedIcdCode"
                    control={control}
                    render={({ fieldState, formState, field: { value }, ...props }) => (
                      <StyleAutocomplete
                        {...props}
                        options={suggestedIcdCodes}
                        getOptionLabel={(option: any) => option.label}
                        value={
                          value
                            ? suggestedIcdCodes.find((code) => code.value === value) || null
                            : null
                        }
                        onChange={handleSuggestedIcdCodeChange}
                        disabled={suggestedIcdCodes.length === 0}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            margin="normal"
                            fullWidth
                            label={t('patient.providerFeedback.form.suggestedIcdCode')}
                            error={!!errors.suggestedIcdCode}
                            helperText={errors.suggestedIcdCode?.message}
                            variant="outlined"
                          />
                        )}
                      />
                    )}
                  />

                  <Controller
                    name="otherIcdCode"
                    control={control}
                    render={({ field }) => (
                      <StyleTextField
                        sx={{ mt: 2 }}
                        {...field}
                        disabled={disabledOtherIcdCodes}
                        fullWidth
                        label={t('patient.providerFeedback.form.otherIcdCodeField')}
                        error={!!errors.otherIcdCode}
                        helperText={errors.otherIcdCode?.message}
                        variant="outlined"
                      />
                    )}
                  />
                </Box>

                <SubmitButton
                  content={
                    diagnosisId
                      ? t('patient.providerFeedback.form.save')
                      : t('patient.providerFeedback.form.add')
                  }
                  matches={!isDesktop}
                  pageErrors={pageErrors}
                />
              </Box>
            )}
          </RoundedContainer>
          {isDesktop
            ? renderDesktopData(sortedPatientDiagnoses(patientData?.patientDiagnoses || []))
            : renderMobileData(sortedPatientDiagnoses(patientData?.patientDiagnoses || []))}
          <PopupModal
            isOpen={isOpenPopup}
            contentPopup={contentPopup}
            onClick={handleDeletePatientDiagnosis}
          />
        </Container>
      </DashboardLayout>
    </Box>
  );
}
